import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, ref } from 'vue';
import moment from 'moment';
import {
    DeliveryListGenerator,
    DeliveryTable,
    LogisticsAddresses,
    LogisticsTopBar,
    ParsingOverlay,
    StepNavigator,
} from '@/modules/Logistics/components';
import Delivery from '@/modules/Logistics/api/models/Delivery';
import DeliveryItem from '@/modules/Logistics/api/models/DeliveryItem';
import DeliveryRepository from '@/modules/Logistics/api/repositories/DeliveryRepository';
import Vehicle from '@/modules/Logistics/api/models/Vehicle';
import VehicleRepository from '@/modules/Logistics/api/repositories/VehicleRepository';
import MontageTeam from '@/modules/Logistics/api/models/MontageTeam';
import MontageTeamRepository from '@/modules/Logistics/api/repositories/MontageTeamRepository';
import DeliveryRegion from '@/models/DeliveryRegion';
import DeliveryRegionRepository from '@/repositories/DeliveryRegionRepository';
import Address from '@/models/Address';
import ProjectSearchRepository from '@/repositories/ProjectSearchRepository';
import { normalizeDeliveryOrders } from '@/modules/Logistics/helpers/DeliveryOrderHelper';
import { useDeliveryFilters } from '@/modules/Logistics/composables/useDeliveryFilters';
import {
    buildDeliveryFormData,
    buildPrintSpreadsheetData,
    formatDateString,
    inferDayFromDeliveryDate,
} from '@/modules/Logistics/helpers/LogisticsParserHelper';
import { computeDatePrintedStatus } from '@/modules/Logistics/helpers/LogisticsTableHelper';
import { formatDateToDateString } from '@/helpers/LocaleHelper';
import type {
    AddressRow,
    DeliveryCreatePayload,
    DeliveryDateGroup,
    DeliveryItemUpdatePayload,
    DeliveryListFormData,
    DeliveryListItem,
    DeliveryListPrintData,
    DeliveryMeta,
    LogisticsStep,
    SpreadsheetData,
} from '@/modules/Logistics/interfaces/LogisticsInterfaces';
import type {
    DeliveryInternalOrder,
    DeliveryOrder,
    SelectedInternalOrdersByOrder,
} from '@/modules/Logistics/interfaces/DeliveryOrderInterfaces';
import OrderStatusRepository from '@/repositories/OrderStatusRepository';


export default /*#__PURE__*/_defineComponent({
  __name: 'Deliveries',
  setup(__props) {

const { proxy } = getCurrentInstance()!;

/* === View State === */

const showGenerator = ref(false);
const generatorRef = ref<InstanceType<typeof DeliveryListGenerator> | null>(null);
const currentStepIndex = ref(0);
const searchQuery = ref('');
const activeTab = ref<'deliveries' | 'addresses'>('deliveries');
const isParsing = ref(false);
const isDeliveriesTableLoading = ref(false);
const editingDeliveryId = ref<string | null>(null);

/* === Generator State === */

const selectedOrderIds = ref<string[]>([]);
const selectedInternalOrderIds = ref<number[]>([]);
const persistedInternalOrderIds = ref<number[]>([]);
const ordersLoading = ref(false);
const printData = ref<DeliveryListPrintData>(createEmptyPrintData());

/* === Filters === */

const {
    selectedDeliveryDates,
    selectedDeliveryClientId,
    selectedOrderDeliveryRegionId,
    deliveryClients,
    deliveryClientSearchLoading,
    handleDeliveryDatesChange,
    handleDeliveryClientSearch,
    handleDeliveryClientChange,
    handleOrderDeliveryRegionChange,
    resetDeliveryFilters,
} = useDeliveryFilters({
    selectedOrderIds,
    selectedInternalOrderIds,
    ordersLoading,
    notifyError,
});

const deliveryOrders = computed<DeliveryOrder[]>(() =>
    selectedDeliveryDates.value.length ? normalizeDeliveryOrders(ProjectSearchRepository.getAll()) : [],
);

const selectedOrders = computed<DeliveryOrder[]>(() => {
    const selectedIds = new Set(selectedOrderIds.value);
    return deliveryOrders.value.filter((order) => selectedIds.has(order.id));
});

const selectedInternalOrdersByOrder = computed<SelectedInternalOrdersByOrder[]>(() => {
    const selectedIds = new Set(selectedInternalOrderIds.value);

    return selectedOrders.value
        .map((order) => ({
            order,
            internalOrders: order.projectInternalOrders.filter((internalOrder) =>
                selectedIds.has(Number(internalOrder.id)),
            ),
        }))
        .filter((group) => group.internalOrders.length > 0);
});

const orderStatuses = computed(() => OrderStatusRepository.getAll());

const totalPieces = computed(() => sumSpreadsheetColumn(printData.value.spreadsheetData, 5));
const isEditMode = computed(() => editingDeliveryId.value !== null);

const generatorSteps = computed<LogisticsStep[]>(() => [
    {
        id: 0,
        title: 'Orders',
        description: 'Select orders',
        isActive: currentStepIndex.value === 0,
        isCompleted: currentStepIndex.value > 0,
        canProceed: selectedOrderIds.value.length > 0,
        proceedButtonText: 'Next',
        class: 'orders-step',
        proceedIcon: 'arrow-right',
        action: 'select-orders',
        showAlternativeButton: false,
        canGoBack: false,
        showBackButton: false,
    },
    {
        id: 1,
        title: 'Internal orders',
        description: 'Select internal orders',
        isActive: currentStepIndex.value === 1,
        isCompleted: currentStepIndex.value > 1,
        canProceed: selectedInternalOrderIds.value.length > 0,
        proceedButtonText: 'Next',
        class: 'internal-orders-step',
        proceedIcon: 'arrow-right',
        action: 'select-internal-orders',
        showAlternativeButton: false,
        canGoBack: true,
        showBackButton: true,
    },
    {
        id: 2,
        title: 'Print',
        description: 'Review and print',
        isActive: currentStepIndex.value === 2,
        isCompleted: false,
        canProceed: printData.value.spreadsheetData !== null,
        proceedButtonText: 'Print',
        alternativeButtonText: 'Save',
        class: 'print-step',
        proceedIcon: 'print',
        action: 'print',
        alternativeAction: 'save',
        showAlternativeButton: true,
        canGoBack: selectedOrders.value.length > 0,
        showBackButton: selectedOrders.value.length > 0,
    },
]);

const currentStep = computed(() => generatorSteps.value[currentStepIndex.value] || null);

/* === Delivery Lists === */

const deliveryListsForTable = computed<DeliveryListItem[]>(() =>
    DeliveryRepository.getAll().map((delivery) => ({
        id: Number(delivery.id),
        date: formatDateToDateString(delivery.date),
        number: String(delivery.id),
        printed: delivery.isPrinted ? '✅' : '❌',
        weekday: inferDayFromDeliveryDate(delivery.date),
    })),
);

const deliveryDateGroups = computed<DeliveryDateGroup[]>(() => {
    const groups = new Map<string, DeliveryListItem[]>();

    deliveryListsForTable.value.forEach((item) => {
        groups.set(item.date, [...(groups.get(item.date) || []), item]);
    });

    return Array.from(groups.entries())
        .map(([date, items]) => ({
            id: `date-${date}`,
            date,
            weekday: items[0]?.weekday || '',
            number: String(items.length),
            printed: computeDatePrintedStatus(items),
            isDateRow: true as const,
            children: items.map((item, index) => ({
                ...item,
                date: `${date}/${index + 1}${item?.regionName ? ` ${item.regionName}` : ''}`,
                number: '',
                weekday: '',
            })),
        }))
        .sort((a, b) => moment(b.date, 'DD.MM.YYYY').valueOf() - moment(a.date, 'DD.MM.YYYY').valueOf());
});

const currentDelivery = computed(() =>
    editingDeliveryId.value ? DeliveryRepository.getById(editingDeliveryId.value) : null,
);

/* === Dependencies and Addresses === */

const montageTeams = computed(() => MontageTeamRepository.getAll());
const vehicles = computed(() => VehicleRepository.getAll());
const deliveryRegions = computed(() => DeliveryRegionRepository.getAll());

const addressesData = ref<AddressRow[]>([]);
const addressesLoading = ref(false);
const addressesSearchQuery = ref('');
const selectedAddressDays = ref<string[]>([]);
const selectedDeliveryRegion = ref<string | null>(null);
const ADDRESSES_PAGE_SIZE = 500;

/* === Header === */

const topBarTitle = computed(() => {
    if (!showGenerator.value) {
        return proxy.$t('Dates of delivery') as string;
    }
    return proxy.$t(isEditMode.value ? 'Edit Delivery Date' : 'New Delivery Date') as string;
});

const topBarSubtitle = computed(() => {
    if (showGenerator.value && generatorRef.value) {
        return generatorRef.value.currentStepSubtitle;
    }
    return `${deliveryListsForTable.value.length} ${proxy.$t('delivery lists')}`;
});

/* === Initial Data === */

(async function loadInitialData() {
    await Promise.all([fetchDeliveries(), fetchDependencies()]);
})();

async function fetchDeliveries() {
    try {
        isParsing.value = true;
        await Delivery.fetchAll();
    } catch (error) {
        notifyError('Failed to fetch delivery lists.', error);
    } finally {
        isParsing.value = false;
    }
}

async function fetchDependencies() {
    try {
        await Promise.all([Vehicle.fetchAll(), MontageTeam.fetchAll(), DeliveryRegion.getAll()]);
    } catch (error) {
        notifyError('Failed to fetch necessary data.', error);
    }
}

async function fetchAddressesByFilters(days: string[], deliveryRegion: string | null) {
    if (!days.length) {
        addressesData.value = [];
        return;
    }

    try {
        addressesLoading.value = true;
        const addresses = await Address.getByFilters(days, deliveryRegion, ADDRESSES_PAGE_SIZE);
        addressesData.value = addresses.map((address) => {
            console.log(address);
            const client = address.addressClients?.[0];

            return {
                clientName: client?.name || '',
                address: [address.streetNameAndNumber, address.poNumber, address.city].filter(Boolean).join(', '),
                clientId: client?.id || '',
            };
        });
    } catch (error) {
        addressesData.value = [];
        notifyError('Failed to fetch necessary data.', error);
    } finally {
        addressesLoading.value = false;
    }
}

/* === Generator Navigation === */

async function handleNext(action?: string) {
    if (action === 'select-orders') {
        selectedInternalOrderIds.value = buildInternalOrderIds(
            selectedOrders.value.flatMap(({ projectInternalOrders }) => projectInternalOrders),
        );
        currentStepIndex.value = 1;
        return;
    }

    if (action === 'select-internal-orders') {
        const prepared = isEditMode.value
            ? await addNewOrdersAndAdjustPrintData()
            : await saveDeliveryAndPreparePrintData();

        if (prepared) {
            currentStepIndex.value = 2;
        }
        return;
    }

    if (action === 'save') {
        await saveDelivery();
        return;
    }

    if (action === 'print') {
        await saveAndPrintDelivery();
    }
}

function handleBack() {
    if (currentStepIndex.value > 0) {
        currentStepIndex.value -= 1;
    }
}

function handleStepClick(step: LogisticsStep, stepIndex: number) {
    if (step.isActive || !step.isCompleted) {
        return;
    }
    currentStepIndex.value = stepIndex;
}

function handleOrderSelectionChange(orderIds: string[]) {
    selectedOrderIds.value = orderIds;
    const availableInternalOrderIds = new Set(
        buildInternalOrderIds(selectedOrders.value.flatMap(({ projectInternalOrders }) => projectInternalOrders)),
    );
    selectedInternalOrderIds.value = selectedInternalOrderIds.value.filter((id) => availableInternalOrderIds.has(id));
}

function handleInternalOrderSelectionChange(internalOrderIds: number[]) {
    selectedInternalOrderIds.value = [...new Set([...persistedInternalOrderIds.value, ...internalOrderIds])];
}

function buildInternalOrderIds(internalOrders: DeliveryInternalOrder[]): number[] {
    return internalOrders.map(({ id }) => Number(id));
}

/* === Delivery Persistence === */

function buildDeliveryCreatePayload(): DeliveryCreatePayload {
    return {
        data: buildInternalOrderIds(
            selectedInternalOrdersByOrder.value.flatMap(({ internalOrders }) => internalOrders),
        ),
    };
}

function buildDeliverySavePayload(): DeliveryItemUpdatePayload[] {
    return extractSpreadsheetRows();
}

async function saveDelivery(): Promise<boolean> {
    try {
        isParsing.value = true;
        const delivery = isEditMode.value ? await updateDelivery() : await createDelivery();

        if (!delivery) {
            return false;
        }

        editingDeliveryId.value = String(delivery.id);
        return true;
    } catch (error) {
        notifyError(isEditMode.value ? 'Failed to update delivery list.' : 'Failed to create delivery list.', error);
        return false;
    } finally {
        Delivery.fetchAll();
        isParsing.value = false;
    }
}

async function saveDeliveryAndPreparePrintData(): Promise<boolean> {
    const saved = await saveDelivery();
    if (!saved) {
        return false;
    }

    persistedInternalOrderIds.value = [...selectedInternalOrderIds.value];
    await nextTick();

    return buildPrintDataForCurrentDelivery();
}

async function addNewOrdersAndAdjustPrintData(): Promise<boolean> {
    const deliveryId = editingDeliveryId.value;
    if (!deliveryId) {
        return false;
    }

    const persistedIds = new Set(persistedInternalOrderIds.value);
    const newInternalOrderIds = selectedInternalOrderIds.value.filter((id) => !persistedIds.has(id));

    try {
        isParsing.value = true;

        if (newInternalOrderIds.length) {
            await addInternalOrdersToList(deliveryId, newInternalOrderIds);
            await Delivery.fetchById(deliveryId);
            persistedInternalOrderIds.value = [...selectedInternalOrderIds.value];
            await nextTick();
        }

        return buildPrintDataForCurrentDelivery();
    } catch (error) {
        notifyError('Failed to add internal orders to delivery list.', error);
        return false;
    } finally {
        isParsing.value = false;
    }
}

async function addInternalOrdersToList(deliveryId: string, internalOrderIds: number[]): Promise<void> {
    await Promise.all(
        internalOrderIds.map((internalOrderId) => Delivery.addNewInternalOrderToList(deliveryId, internalOrderId)),
    );
}

function buildPrintDataForCurrentDelivery(): boolean {
    if (!editingDeliveryId.value || !currentDelivery.value) {
        return false;
    }

    printData.value.spreadsheetData = buildPrintSpreadsheetData(currentDelivery.value);
    printData.value.formData = buildDeliveryFormData(currentDelivery.value, printData.value.formData);

    return true;
}

async function saveAndPrintDelivery() {
    if (printData.value.formData) {
        printData.value.formData.isPrinted = true;
    }

    if (await saveDelivery()) {
        await printDeliveryPDF();
    }

    if (printData.value.formData) {
        printData.value.formData.isPrinted = false;
    }
}

async function createDelivery(): Promise<Delivery> {
    const payload = buildDeliveryCreatePayload();
    const delivery = await Delivery.createNew(payload);
    proxy.$notification.success({
        message: proxy.$t('Success') as string,
        description: proxy.$t('Delivery list created successfully.') as string,
    });
    return delivery;
}

async function updateDelivery(): Promise<Delivery | null> {
    const deliveryId = editingDeliveryId.value;
    if (!deliveryId) {
        return null;
    }

    await syncDeliveryItems(buildDeliverySavePayload());
    return Delivery.updateExisting(deliveryId, buildDeliveryMeta());
}

async function syncDeliveryItems(rows: DeliveryItemUpdatePayload[]) {
    if (!editingDeliveryId.value || !currentDelivery.value?.items?.length) {
        return;
    }

    const existingItems = currentDelivery.value.items;
    const rowPositions = new Set(rows.map(({ position }) => position));
    const itemsToDelete = existingItems.filter((item) => !rowPositions.has(item.position));
    const rowsToUpdate = rows.filter((row) => existingItems.some((item) => item.position === row.position));

    await Promise.all([
        ...itemsToDelete.map((item) => DeliveryItem.deleteExisting(item.id)),
        ...rowsToUpdate.map((row) => {
            const item = existingItems.find((candidate) => candidate.position === row.position)!;
            return DeliveryItem.updateExisting(item.id, row);
        }),
    ]);
}

async function processDeletingDeliveries(ids: string[]) {
    try {
        isDeliveriesTableLoading.value = true;
        await Promise.all(ids.map((id) => Delivery.deleteExisting(id)));
        proxy.$notification.success({
            message: proxy.$t('Success') as string,
            description: `${ids.length} ${proxy.$t('delivery list(s) deleted.') as string}`,
        });
    } catch (error) {
        notifyError('Failed to delete delivery lists.', error);
    } finally {
        isDeliveriesTableLoading.value = false;
    }
}

async function printDeliveryPDF() {
    if (!editingDeliveryId.value) {
        return;
    }

    try {
        isParsing.value = true;
        const pdfBlob = await Delivery.getPDF(editingDeliveryId.value);
        window.open(URL.createObjectURL(pdfBlob), '_blank');
    } catch (error) {
        notifyError('Failed to load PDF', error);
    } finally {
        isParsing.value = false;
    }
}

function buildDeliveryMeta(): DeliveryMeta {
    const formData = printData.value.formData;
    const meta: DeliveryMeta = {
        date: formatDateString(formData?.deliveryDate || ''),
        vehicle: formData?.vehicle || '',
        driver: formData?.driver || '',
        departureTime: formatDateString(formData?.departureTime || ''),
        arrivalTime: formatDateString(formData?.arrivalTime || ''),
        finalKilometers: formData?.finalKilometers ? parseInt(formData.finalKilometers, 10) : null,
        overallComment: formData?.overallComment || '',
    };

    if (typeof formData?.isPrinted === 'boolean') {
        meta.isPrinted = formData.isPrinted;
    }

    return meta;
}

function extractSpreadsheetRows(): DeliveryItemUpdatePayload[] {
    const spreadsheet = printData.value.spreadsheetData;
    if (!spreadsheet?.rows) {
        return [];
    }

    return Object.keys(spreadsheet.rows)
        .map(Number)
        .filter((rowIndex) => rowIndex > 0 && spreadsheet.rows[rowIndex]?.cells?.[0]?.style !== 0)
        .sort((a, b) => a - b)
        .map((rowIndex) => {
            const cells = spreadsheet.rows[rowIndex]?.cells || {};
            const addressParts = (cells[1]?.text || '').split(',').map((part) => part.trim());
            return {
                position: parseInt(cells[0]?.text || '0', 10),
                clientName: addressParts[0] || '',
                city: addressParts[1] || '',
                street: addressParts.slice(2).join(', '),
                notes: cells[2]?.text || null,
                contactPhone: cells[3]?.text || null,
                payment: cells[4]?.text || null,
                pieces: parseInt(cells[5]?.text || '0', 10),
            };
        });
}

/* === View and Form Events === */

function handleNewDeliveryList() {
    resetGenerator();
    showGenerator.value = true;
}

function handleBackToList() {
    showGenerator.value = false;
    resetGenerator();
}

async function handleRowClick(record: DeliveryListItem | DeliveryDateGroup) {
    if (record.isDateRow) {
        return;
    }

    editingDeliveryId.value = String(record.id);
    await Delivery.fetchById(editingDeliveryId.value);
    printData.value = {
        spreadsheetData: buildPrintSpreadsheetData(currentDelivery.value),
        formData: buildDeliveryFormData(currentDelivery.value, printData.value.formData),
    };
    showGenerator.value = true;
    currentStepIndex.value = 2;
}

function handleDeleteRows(ids: number[], onConfirm: () => void) {
    if (!ids.length) {
        return;
    }

    proxy.$confirm({
        title: proxy.$t('Are you sure you want to delete selected deliveries') as string,
        iconType: 'exclamation-circle',
        okText: proxy.$t('Yes') as string,
        okType: 'danger',
        cancelText: proxy.$t('Cancel') as string,
        onOk: () => {
            onConfirm();
            return processDeletingDeliveries(ids.map(String));
        },
    });
}

function handleDaysChange(days: string[]) {
    selectedAddressDays.value = days;
    fetchAddressesByFilters(days, selectedDeliveryRegion.value);
}

function handleDeliveryRegionChange(deliveryRegion: string | null) {
    selectedDeliveryRegion.value = deliveryRegion;
    fetchAddressesByFilters(selectedAddressDays.value, deliveryRegion);
}

function handleFormFieldUpdate(
    field: keyof DeliveryListFormData,
    value: DeliveryListFormData[keyof DeliveryListFormData],
) {
    if (printData.value.formData) {
        (printData.value.formData[field] as DeliveryListFormData[typeof field]) = value;
    }
}

function handleSpreadsheetChange(data: SpreadsheetData | null) {
    printData.value.spreadsheetData = data;
}

/* === Small Utilities === */

function resetGenerator() {
    currentStepIndex.value = 0;
    editingDeliveryId.value = null;
    resetDeliveryFilters();
    selectedOrderIds.value = [];
    selectedInternalOrderIds.value = [];
    persistedInternalOrderIds.value = [];
    printData.value = createEmptyPrintData();
}

function createEmptyPrintData(): DeliveryListPrintData {
    return {
        spreadsheetData: null,
        formData: {
            deliveryDate: '',
            vehicle: '',
            driver: '',
            departureTime: '',
            arrivalTime: '',
            finalKilometers: '',
            isPrinted: null,
            overallComment: '',
        },
    };
}

function sumSpreadsheetColumn(spreadsheet: SpreadsheetData | null, columnIndex: number): number {
    if (!spreadsheet?.rows) {
        return 0;
    }

    return Object.keys(spreadsheet.rows)
        .map(Number)
        .filter((rowIndex) => rowIndex > 0 && spreadsheet.rows[rowIndex]?.cells?.[columnIndex]?.style !== 0)
        .reduce(
            (total, rowIndex) =>
                total + (parseInt(spreadsheet.rows[rowIndex]?.cells?.[columnIndex]?.text || '0', 10) || 0),
            0,
        );
}

function notifyError(description: string, error: unknown) {
    console.error(description, error);
    proxy.$notification.error({
        message: proxy.$t('Error') as string,
        description: proxy.$t(description) as string,
    });
}

return { __sfc: true,proxy, showGenerator, generatorRef, currentStepIndex, searchQuery, activeTab, isParsing, isDeliveriesTableLoading, editingDeliveryId, selectedOrderIds, selectedInternalOrderIds, persistedInternalOrderIds, ordersLoading, printData, selectedDeliveryDates, selectedDeliveryClientId, selectedOrderDeliveryRegionId, deliveryClients, deliveryClientSearchLoading, handleDeliveryDatesChange, handleDeliveryClientSearch, handleDeliveryClientChange, handleOrderDeliveryRegionChange, resetDeliveryFilters, deliveryOrders, selectedOrders, selectedInternalOrdersByOrder, orderStatuses, totalPieces, isEditMode, generatorSteps, currentStep, deliveryListsForTable, deliveryDateGroups, currentDelivery, montageTeams, vehicles, deliveryRegions, addressesData, addressesLoading, addressesSearchQuery, selectedAddressDays, selectedDeliveryRegion, ADDRESSES_PAGE_SIZE, topBarTitle, topBarSubtitle, fetchDeliveries, fetchDependencies, fetchAddressesByFilters, handleNext, handleBack, handleStepClick, handleOrderSelectionChange, handleInternalOrderSelectionChange, buildInternalOrderIds, buildDeliveryCreatePayload, buildDeliverySavePayload, saveDelivery, saveDeliveryAndPreparePrintData, addNewOrdersAndAdjustPrintData, addInternalOrdersToList, buildPrintDataForCurrentDelivery, saveAndPrintDelivery, createDelivery, updateDelivery, syncDeliveryItems, processDeletingDeliveries, printDeliveryPDF, buildDeliveryMeta, extractSpreadsheetRows, handleNewDeliveryList, handleBackToList, handleRowClick, handleDeleteRows, handleDaysChange, handleDeliveryRegionChange, handleFormFieldUpdate, handleSpreadsheetChange, resetGenerator, createEmptyPrintData, sumSpreadsheetColumn, notifyError, DeliveryListGenerator, DeliveryTable, LogisticsAddresses, LogisticsTopBar, ParsingOverlay, StepNavigator }
}

})